import React, { useState, useEffect } from 'react';
import axios from '../api/axios';
import { User, Briefcase, Trash2, AlertCircle, Crown } from 'lucide-react';
import styles from './DepartmentRosterTab.module.css';

interface RosterMember {
  id: number;
  username: string;
  role: string;
  avatarUrl?: string;
  primaryPosition?: string;
  secondaryPosition?: string;
  tertiaryPosition?: string;
}

interface DepartmentRosterTabProps {
  department: string;
}

const DepartmentRosterTab: React.FC<DepartmentRosterTabProps> = ({ department }) => {
  const [roster, setRoster] = useState<RosterMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedMember, setSelectedMember] = useState<RosterMember | null>(null);
  const [positionSlot, setPositionSlot] = useState<'primary' | 'secondary' | 'tertiary' | null>(null);
  const [processing, setProcessing] = useState(false);

  // Helper to get full avatar URL
  const getFullAvatarUrl = (avatarUrl?: string): string | undefined => {
    if (!avatarUrl) return undefined;
    if (avatarUrl.startsWith('http')) return avatarUrl;
    return `${process.env.REACT_APP_API_URL}${avatarUrl}`;
  };

  useEffect(() => {
    fetchRoster();
  }, [department]);

  const fetchRoster = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/Departments/${department}/roster`);
      console.log('Roster response:', response.data);
      setRoster(response.data);
    } catch (error) {
      console.error('Failed to fetch roster:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemovePosition = async () => {
    if (!selectedMember || !positionSlot) return;

    try {
      setProcessing(true);
      await axios.delete(
        `/Departments/${department}/roster/${selectedMember.id}/position/${positionSlot}`
      );
      
      setSelectedMember(null);
      setPositionSlot(null);
      fetchRoster();
    } catch (error) {
      console.error('Failed to remove position:', error);
      alert('Failed to remove position. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  // Count total positions for statistics
  const countPositions = (member: RosterMember): number => {
    let count = 0;
    if (member.primaryPosition) count++;
    if (member.secondaryPosition) count++;
    if (member.tertiaryPosition) count++;
    return count;
  };

  const getTotalPositionsFilled = (): number => {
    return roster.reduce((sum, member) => sum + countPositions(member), 0);
  };

  // Get member's positions in this department
  const getMemberPositions = (member: RosterMember): { title: string; slot: string }[] => {
    const positions: { title: string; slot: string }[] = [];
    
    if (member.primaryPosition) {
      positions.push({ title: member.primaryPosition, slot: 'primary' });
    }
    if (member.secondaryPosition) {
      positions.push({ title: member.secondaryPosition, slot: 'secondary' });
    }
    if (member.tertiaryPosition) {
      positions.push({ title: member.tertiaryPosition, slot: 'tertiary' });
    }
    
    return positions;
  };



  if (loading) {
    return <div className={styles.loading}>Loading department roster...</div>;
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.stats}>
          <div className={styles.statCard}>
            <User className={styles.statIcon} />
            <div className={styles.statContent}>
              <p className={styles.statValue}>{roster.length}</p>
              <p className={styles.statLabel}>Total Members</p>
            </div>
          </div>
          
          <div className={styles.statCard}>
            <Briefcase className={styles.statIcon} />
            <div className={styles.statContent}>
              <p className={styles.statValue}>
                {getTotalPositionsFilled()}
              </p>
              <p className={styles.statLabel}>Positions Filled</p>
            </div>
          </div>
        </div>
      </div>

      {roster.length === 0 ? (
        <div className={styles.empty}>
          <AlertCircle size={48} />
          <p>No members assigned to this department</p>
        </div>
      ) : (
        <div className={styles.membersGrid}>
          {roster.map((member) => {
            const positions = getMemberPositions(member);
            
            return (
              <div key={member.id} className={styles.memberCard}>
                <div className={styles.userInfo}>
                  {member.avatarUrl ? (
                    <img 
                      src={getFullAvatarUrl(member.avatarUrl)} 
                      alt={member.username} 
                      className={styles.avatar}
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                        e.currentTarget.nextElementSibling?.classList.remove('hidden');
                      }}
                    />
                  ) : (
                    <div className={styles.avatarPlaceholder}>
                      <User size={24} />
                    </div>
                  )}
                  
                  <div className={styles.userDetails}>
                    <div className={styles.username}>
                      {member.username}
                      {member.role === 'Admin' && (
                        <Crown size={16} className={styles.adminBadge} />
                      )}
                    </div>
                    <div className={styles.roleLabel}>{member.role || 'Member'}</div>
                  </div>
                </div>

                <div className={styles.positionsList}>
                  {positions.length > 0 ? (
                    <div className={styles.positionBadges}>
                      {positions.map((pos) => (
                        <span key={pos.slot} className={styles.positionBadge}>
                          {pos.title}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className={styles.noPositions}>No positions in this department</p>
                  )}
                </div>

                {positions.length > 0 && (
                  <button
                    className={styles.removeBtn}
                    onClick={() => setSelectedMember(member)}
                  >
                    <Trash2 size={16} />
                    Remove Position
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {selectedMember && (
        <div className={styles.modalOverlay} onClick={() => setSelectedMember(null)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h3>Remove Position</h3>
            <p>Select which position to remove from {selectedMember.username}:</p>
            
            <div className={styles.positionSelectList}>
              {selectedMember.primaryPosition && (
                <label className={styles.positionOption}>
                  <input
                    type="radio"
                    name="position"
                    value="primary"
                    checked={positionSlot === 'primary'}
                    onChange={() => setPositionSlot('primary')}
                  />
                  <span>Primary: {selectedMember.primaryPosition}</span>
                </label>
              )}
              
              {selectedMember.secondaryPosition && (
                <label className={styles.positionOption}>
                  <input
                    type="radio"
                    name="position"
                    value="secondary"
                    checked={positionSlot === 'secondary'}
                    onChange={() => setPositionSlot('secondary')}
                  />
                  <span>Secondary: {selectedMember.secondaryPosition}</span>
                </label>
              )}
              
              {selectedMember.tertiaryPosition && (
                <label className={styles.positionOption}>
                  <input
                    type="radio"
                    name="position"
                    value="tertiary"
                    checked={positionSlot === 'tertiary'}
                    onChange={() => setPositionSlot('tertiary')}
                  />
                  <span>Tertiary: {selectedMember.tertiaryPosition}</span>
                </label>
              )}
            </div>

            <div className={styles.modalActions}>
              <button
                className={styles.cancelBtn}
                onClick={() => {
                  setSelectedMember(null);
                  setPositionSlot(null);
                }}
                disabled={processing}
              >
                Cancel
              </button>
              <button
                className={styles.confirmBtn}
                onClick={handleRemovePosition}
                disabled={!positionSlot || processing}
              >
                {processing ? 'Removing...' : 'Confirm Remove'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DepartmentRosterTab;
